import { Link } from 'react-router';
import { formatPeso, type PriceStats } from '@tahi/core';
import { Money, ScreenHeader } from '../../../ui';
import { copy, LIST_BACK } from '../copy';
import { unitPriceText } from '../format';
import { usePriceStats } from '../hooks';
import { CARD, FOCUS, SCREEN } from '../styles';

/** "▲ ₱3/kg" or "▼ ₱15", or null when there is nothing to compare. */
export function changeText(s: PriceStats): string | null {
  if (s.changeValue === null) return null;
  if (s.changeValue === 0) return copy.noChange;
  const amount =
    s.basis === 'unit' && s.unit
      ? unitPriceText(Math.abs(s.changeValue), s.unit)
      : formatPeso(Math.abs(s.changeValue));
  return s.changeValue > 0 ? copy.up(amount) : copy.down(amount);
}

/** Every item bought with its own price, most recently bought first. */
export function PricesScreen() {
  const stats = usePriceStats();
  const rows = [...(stats?.entries() ?? [])].sort(([, a], [, b]) =>
    a.last.date < b.last.date ? 1 : a.last.date > b.last.date ? -1 : 0,
  );
  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.prices} back={LIST_BACK} />
      {stats === undefined ? (
        <p className="text-muted">{copy.loading}</p>
      ) : rows.length === 0 ? (
        <p className="text-muted">{copy.noPrices}</p>
      ) : (
        <ul
          aria-label={copy.prices}
          className={`${CARD} flex flex-col divide-y divide-divider py-1`}
        >
          {rows.map(([key, s]) => {
            const change = changeText(s);
            return (
              <li key={key}>
                <Link
                  to={`/m/shopping/prices/${encodeURIComponent(key)}`}
                  className={`flex min-h-12 items-center justify-between gap-3 rounded-xl py-2 ${FOCUS}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-fg">{s.last.name}</span>
                    <span className="block truncate text-xs text-muted">
                      {[copy.timesBought(s.points.length), change].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <Money
                    minor={s.last.priceMinor}
                    cents="auto"
                    animate={false}
                    className="shrink-0 text-fg"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
